import { Component, OnInit, OnDestroy } from '@angular/core';
import { ShopService } from '@services/shops/shop.service';
import { Shop } from '@services/shops/shop.model';
import { Observable, Subject } from 'rxjs';
import {
  ExtensionComponentType,
  ICheckoutExtensions,
  DatePickerExtension,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';

import { UntypedFormGroup, UntypedFormBuilder, UntypedFormArray } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { CheckoutReducer } from '@pages/checkout-app/checkout-store/reducers';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { takeUntil } from 'rxjs/operators';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import { ViewMode } from '@pages/checkout-app/checkout-layout/main/components/extensions/models';
import { DatePickerConfig, DefaultVendorConfigs, ExternalVendor } from './model';
import { DatePickerDto } from './date-picker.dto';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { PlacementModeConfig } from '@pages/checkout-app/checkout-layout/main/components/extensions/components/shared/config/config';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
@Component({
  selector: 'delivery-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  standalone: false,
})
export class DatePickerComponent implements OnInit, OnDestroy {
  @onDestroyComplete() destroy$ = new Subject();
  ExternalVendorType = DatePickerExtension.ExternalVendorType;
  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;
  datePickerConfig = [...DatePickerConfig];
  placementModeConfig = [...PlacementModeConfig];
  shopCurrencySymbol: string;
  formGroup: UntypedFormGroup;
  externalVendors: ExternalVendor[] = [];
  shop$: Observable<Shop>;
  allowExternalVendors: boolean = false;
  constructor(
    private shopService: ShopService,
    private rulesDynamicFormService: RulesDynamicFormService,
    private formBuilder: UntypedFormBuilder,
    private store: Store<fromCheckoutReducer.State>,
    private extensionsService: ExtensionsService,
  ) {}

  ngOnDestroy() {}

  ngOnInit() {
    this.shop$ = this.shopService.shop$;
    this.allowExternalVendors = this.shopService?.metadata?.datePickerExternalVendorIntegrationsEnabled;
    this.shopCurrencySymbol = this.shopService.shopCurrencySymbol;
    this.formGroup = this.formBuilder.group(this.initForm());
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.DATE_PICKER);
    this.extensionsService.save$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleSave();
    });

    this.loadedExtension$.pipe(takeUntil(this.destroy$)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.formGroup = this.formBuilder.group(this.initForm(extension.template, extension.testMode));
        this.extensionsService.setFormValid(this.formGroup.valid);
      }
    });

    this.initChangesForExampleView();
    this.formGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.extensionsService.setFormValid(this.formGroup.valid);
    });
    this.extensionsService.setFormValid(this.formGroup.valid);
  }

  initChangesForExampleView() {
    const exampleTemplate = new DatePickerDto().createExampleTemplate();
    this.dispatchExampleViewValues(this.loadedExtension?.template ?? exampleTemplate);

    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
    });
  }

  dispatchExampleViewValues(values) {
    if (!values) {
      return;
    }

    const template = {
      internalName: values.internalName,
      type: values.type,
      title: values.title,
      description: values.description,
      placementMode: values.placementMode,
      externalVendors: values.externalVendors,
    };

    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.DATE_PICKER]: {
        mode,
        template,
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  initForm(loadedValues?: any, testModeValue?: boolean) {
    const newValues = {};
    // handle template
    this.datePickerConfig.forEach((field) => {
      if (field.fieldName == 'activeShippingDays') {
        // this handled inside the component;
        newValues[field.fieldName] = new UntypedFormArray([]);
      } else if (field.type == 'formArray') {
        newValues[field.fieldName] = this.rulesDynamicFormService.initRulesFormArray(
          loadedValues && loadedValues[field.fieldName],
          field.rulesConfig,
        );
      } else {
        newValues[field.fieldName] = [
          loadedValues ? loadedValues[field.fieldName] : field.defaultValue,
          field.validators,
        ];
      }
    });

    // handle main fields (not sure loop is needed atm)
    newValues['testMode'] = testModeValue ?? false;
    newValues['externalVendors'] = loadedValues ? loadedValues['externalVendors'] : [];

    this.initializeExternalVendors(loadedValues ? loadedValues['externalVendors'] : []);
    return newValues;
  }

  handleSave() {
    this.updateExternalVendorFormValue();
    const template = { ...this.formGroup.value };
    const datePickerDTO = new DatePickerDto();

    const datePickerObject: Partial<ICheckoutExtensions> = {
      template: datePickerDTO.createDatePickerTemplateDTO(template),
      type: ExtensionComponentType.DATE_PICKER,
      testMode: template.testMode ?? false,
    };

    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: datePickerObject }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: datePickerObject }));
    }
  }

  initializeExternalVendors(loadedVendors: DatePickerExtension.Integrations[] = []) {
    const vendorsArray = Array.isArray(loadedVendors) ? loadedVendors : [];

    this.externalVendors = Object.entries(this.ExternalVendorType).map(([key, value]) => {
      const vendorType = value as DatePickerExtension.ExternalVendorType;
      const loadedVendor = vendorsArray.find((v) => v[vendorType] !== undefined);
      const loadedConfig = loadedVendor?.[vendorType];

      return {
        type: vendorType,
        label: this.formatVendorLabel(key),
        checked: !!loadedConfig,
        config: { ...(loadedConfig ?? DefaultVendorConfigs[vendorType]) },
      };
    });
  }
  updateExternalVendorFormValue() {
    const checkedVendors = this.externalVendors
      .filter((v) => v.checked)
      .map((v) => ({
        [v.type]: v.config,
      }));
    this.formGroup.get('externalVendors')?.setValue(checkedVendors);
  }
  onVendorCheckedChange(vendor: ExternalVendor, checked: boolean) {
    vendor.checked = checked;
    this.updateExternalVendorFormValue();
  }

  onVendorConfigChange<T extends DatePickerExtension.ExternalVendorType>(
    vendor: ExternalVendor<T>,
    field: keyof DatePickerExtension.Integrations[T],
    value: string,
  ) {
    if (field === 'skuPrefix' && vendor.type === DatePickerExtension.ExternalVendorType.URBAN_STEMS) {
      const skuPrefixes = value.split(',').map((sku) => sku.trim().toLowerCase());
      vendor.config = { ...vendor.config, skuPrefix: skuPrefixes };
    } else {
      vendor.config = { ...vendor.config, [field]: value };
    }
    this.updateExternalVendorFormValue();
  }
  private formatVendorLabel(enumKey: string): string {
    return enumKey
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  getConfigKeys(vendorType: DatePickerExtension.ExternalVendorType): string[] {
    return Object.keys(DefaultVendorConfigs[vendorType]);
  }

  formatConfigLabel(field: string): string {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  }
}
